import * as React from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { theme } from "./theme";

import Footer from "./navigation/Footer";
import TermsOfService from "./navigation/TermsOfService";
import PrivacyPolicy from "./navigation/PrivacyPolicy";
import LegalNotice from "./navigation/LegalNotice";

// Pages
import Welcome from "./pages/Welcome";
import Entry from "./pages/auth/Entry";
import Dashboard from "./pages/Dashboard";
import CommandExecutor from "./pages/CommandExecutor";
import MissionView from "./pages/MissionView";
import WorkshopWaiting from "./pages/WorkshopWaiting";
import UplinkWaiting from "./pages/UplinkWaiting";
import LaunchPage from "./pages/LaunchPage";
import AdminWorkshops from "./pages/admin/AdminWorkshops";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminMissions from "./pages/admin/AdminMissions";
import AdminPayloads from "./pages/admin/AdminPayloads";
import AdminLaunches from "./pages/admin/AdminLaunches";

import { clearSession, getSession, type Session } from "./api/session";
import SessionBar from "./navigation/SessionBar";
import { routeAfterAuth } from "./pages/utils/workshopRouting";
import { getCurrentSession, signOut } from "./api/queries";

const ROUTE_PAGES: Record<string, string> = {
  "/terms-and-conditions": "Terms",
  "/privacy-policy": "Privacy",
  "/legal-notice": "LegalNotice",
};

const PAGE_ROUTES: Record<string, string> = {
  Terms: "/terms-and-conditions",
  Privacy: "/privacy-policy",
  LegalNotice: "/legal-notice",
};

const NON_RESTORABLE_PAGES = new Set([
  "Welcome",
  "Entry",
  "Terms",
  "Privacy",
  "LegalNotice",
]);

const ROUTED_PATHS = new Set(Object.keys(ROUTE_PAGES));


export default function App() {
  const [currentPage, setCurrentPage] = React.useState("Welcome");
  const [isRestoring, setIsRestoring] = React.useState(true);
  const [session, setSession] = React.useState<Session | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handlePageChange = React.useCallback((page: string) => {
    setCurrentPage(page);
    // Every navigation re-reads the session, so it's the single place that
    // picks up a fresh login/registration (Entry navigates here right after
    // saving one) and a logout (SessionBar navigates to Welcome right after
    // clearing it).
    setSession(getSession());

    if (page === "Welcome" || page === "Entry") {
      localStorage.removeItem("tk.currentPage");
    } else if (!NON_RESTORABLE_PAGES.has(page)) {
      localStorage.setItem("tk.currentPage", page);
    }

    const route = PAGE_ROUTES[page];
    if (route && location.pathname !== route) {
      navigate(route);
    } else if (!route && ROUTED_PATHS.has(location.pathname)) {
      navigate("/");
    }
  }, [location.pathname, navigate]);

  const handleLogout = React.useCallback(() => {
    clearSession();
    void signOut();
    handlePageChange("Welcome");
  }, [handlePageChange]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  React.useEffect(() => {
    setIsRestoring(true);

    const routePage = ROUTE_PAGES[location.pathname];
    if (routePage) {
      setCurrentPage(routePage);
      setIsRestoring(false);
      return;
    }

    let cancelled = false;

    (async () => {
      // The Supabase Auth session is what actually proves a login; the saved
      // session (api/session.ts) is only a local copy of who that is. Without
      // a matching Supabase session -- expired, signed out elsewhere, or
      // left over from before real logins existed -- it counts as logged out.
      const { data } = await getCurrentSession();
      if (cancelled) return;

      const saved = getSession();
      const authEmail = data.session?.user.email?.toLowerCase();
      const existingSession = saved && authEmail === saved.email.toLowerCase() ? saved : null;
      setSession(existingSession);

      if (!existingSession) {
        clearSession();
        if (data.session) void signOut();
        setCurrentPage("Welcome");
        localStorage.removeItem("tk.currentPage");
        setIsRestoring(false);
        return;
      }

      // Workshop status (chosen mission / unlocked or not) is the source of
      // truth for where to land, not the last page visited -- an admin could
      // have changed it since the last load. The one exception: a participant
      // who had gone back to the Dashboard stays there on reload instead of
      // being sent on to the access-code screen or the sheet again. The
      // Dashboard shows whatever their current status is, so that is safe.
      const lastPage = localStorage.getItem("tk.currentPage");
      await routeAfterAuth(existingSession.email, (page) => {
        if (cancelled) return;
        const isParticipantPage = !page.startsWith("Admin");
        setCurrentPage(lastPage === "Dashboard" && isParticipantPage ? "Dashboard" : page);
      });
      if (!cancelled) setIsRestoring(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const pages: { [key: string]: JSX.Element } = {
    Welcome: <Welcome handlePageChange={handlePageChange} />,
    Entry: <Entry handlePageChange={handlePageChange} />,
    Dashboard: <Dashboard handlePageChange={handlePageChange} />,
    CommandExecutor: <CommandExecutor handlePageChange={handlePageChange} />,
    MissionView: <MissionView handlePageChange={handlePageChange} />,
    WorkshopWaiting: <WorkshopWaiting handlePageChange={handlePageChange} />,
    UplinkWaiting: <UplinkWaiting handlePageChange={handlePageChange} />,
    Launch: <LaunchPage handlePageChange={handlePageChange} />,
    AdminWorkshops: <AdminWorkshops handlePageChange={handlePageChange} />,
    AdminUsers: <AdminUsers handlePageChange={handlePageChange} />,
    AdminMissions: <AdminMissions handlePageChange={handlePageChange} />,
    AdminPayloads: <AdminPayloads handlePageChange={handlePageChange} />,
    AdminLaunches: <AdminLaunches handlePageChange={handlePageChange} />,

    Terms: <TermsOfService handlePageChange={handlePageChange} />,
    Privacy: <PrivacyPolicy handlePageChange={handlePageChange} />,
    LegalNotice: <LegalNotice handlePageChange={handlePageChange} />,
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          overflowX: "hidden",
          bgcolor: "#F3F4F6",
        }}
      >
        {!isRestoring && session && <SessionBar session={session} onLogout={handleLogout} />}

        <Box sx={{ flex: 1 }}>
          {isRestoring
            ? null
            : pages[currentPage] ?? (
                // Tiles on the Dashboard can point at pages that don't
                // exist yet -- this
                // keeps an unbuilt destination from rendering blank.
                <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center", color: "#6B7280" }}>
                  Coming soon.
                </Box>
              )}
        </Box>

        {!isRestoring && <Footer handlePageChange={handlePageChange} />}
      </Box>
    </ThemeProvider>
  );
}
